/**
 * 「보낸 이력」 — 회의록 안에서 «누구에게 갔는지»를 되짚는 자리.
 *
 * ## ⛔⛔ 이력은 「보냈다」이지 「닿았다」가 «아니다»
 *
 * 메일이 반송돼도 이력에는 **보낸 것으로 남는다** — 그 구별은 **보낸메일함**이 한다.
 * 그래서 「전달됨」·「도착」 같은 말을 쓰지 않는다. 쓰면 사람이 **닿은 것으로 읽는다.**
 *
 * ## ⛔ 「3명」처럼 수만 적지 않는다
 *
 * 되돌리기가 없으니 이 이력이 **유일한 되짚을 길**이다. 그래서 접어도 앞 사람 이름이 남고
 * (`formatRecipientTrail`), 펼치면 **전원**이 보인다.
 *
 * ## ⚠ 「비었다」와 「서버가 아직 안 준다」는 다른 일이다
 *
 * 앞은 「아직 안 보냈다」, 뒤는 「서버가 아직 이것을 줄 줄 모른다」. 문구가 다르고, 뒤일 때는
 * **보내기 단추를 잠근다**(누를 데가 없으므로).
 */
import { useCallback, useEffect, useState } from 'react';
import { Clock } from '../../icons';
import { Badge } from '../../components/shared/Badge';
import { Timeline, type TimelineItem } from '../../components/shared/Timeline';
import { formatDateTime } from '../../lib/base-i18n';
import { tMinutes } from '../../lib/minutes-i18n';
import { listMinutesSends, type MinutesSendRecord, type MinutesSendRecipient } from '../../api/minutes-send';
import { formatRecipientTrail, type MinutesSendChannel } from './minutes-send-core';

/**
 * ★ `gone` 이 2026-09-22 에 붙었다 — 「없는·못 보는 회의록」(404 + `code:'NOT_FOUND'`)이다.
 *   ⛔ `not-served`(서버가 아직 안 낸다)와 **같은 문구로 덮지 않는다** — 하나는 기다리면 되고
 *     하나는 그 회의록이 없다는 뜻이다.
 */
export type SendHistoryState = 'loading' | 'ready' | 'not-served' | 'gone' | 'error';

export interface MinutesSendsHook {
  state: SendHistoryState;
  items: MinutesSendRecord[];
  /**
   * 보낸 뒤 이력을 **다시 읽는다.**
   *
   * ## ★★★ 왜 «꽂지» 않고 «다시 읽나» (2026-09-22 바꿈)
   *
   * 처음 판은 201 응답 객체를 목록 맨 위에 그대로 꽂았다. 그런데 **201 응답과 이력 한 줄은 모양이
   * 다르다** — 201 엔 `sent_at`·`id` 가 없고 `room_id`·`logged` 가 있다. 꽂으면 **방금 보낸 줄이
   * 시각 없이·key 없이** 그려진다.
   *
   * ⛔ 모양을 흉내 내 «가짜 줄»을 만들지도 않는다: 되돌리기가 없는 기능에서 **서버가 적은 줄과
   *   화면이 상상한 줄이 다르면, 사용자가 「무엇이 남았는지」를 영영 확인할 수 없다.**
   *   다시 읽으면 화면에 보이는 것이 **실제로 남은 것**이다.
   *
   * ⚠ `logged === false` 면 **부르지 않는다** — 적히지 않았으니 읽어도 없고, 빈 목록이 「없다」로
   *   보이면 더 헷갈린다. 그때는 화면이 「이력에 남지 않았습니다」를 말한다.
   */
  reload: () => void;
}

/**
 * 이력 한 벌을 읽는다.
 *
 * ★ 상태를 **한 덩이**로 들고 있다 — `items` 를 `rows ?? []` 처럼 렌더마다 새로 만들면 그것을 보는
 *   effect 가 계속 돌아 **시험이 안 끝난다**(그리고 안 끝나는 시험은 «실패로도 안 보인다»).
 */
export function useMinutesSends(minutesId: string): MinutesSendsHook {
  const [state, setState] = useState<SendHistoryState>('loading');
  const [items, setItems] = useState<MinutesSendRecord[]>([]);
  /** 다시 읽기를 부르는 «횟수». 값이 바뀌면 아래 effect 가 한 번 더 돈다. */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setState('loading');
    setItems([]);
    void listMinutesSends(minutesId)
      .then((r) => {
        if (!alive) return;
        if (r.ready === true) {
          setItems(r.items);
          setState('ready');
        } else if (r.ready === 'not-found') {
          setState('gone');
        } else {
          setState('not-served');
        }
      })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
    // ★ `nonce` 가 의존성에 있어 `reload()` 가 이 effect 를 다시 돌린다.
  }, [minutesId, nonce]);

  const reload = useCallback(() => { setNonce((n) => n + 1); }, []);

  return { state, items, reload };
}

/**
 * ★ `export` 는 2026-09-22 에 붙었다(동작은 안 바뀐다) — 보내기 패널의 「이미 보냈습니다」 줄이
 *   **같은 이름**을 써야 한다. 갈래 이름을 두 곳에 적으면 한쪽만 바뀌는 날이 온다.
 */
export const CHANNEL_KEY: Record<MinutesSendChannel, Parameters<typeof tMinutes>[0]> = {
  approval: 'minutes.send.ch_approval',
  message: 'minutes.send.ch_message',
  mail: 'minutes.send.ch_mail',
};

/**
 * 받는 사람 하나를 이력에 적을 글자로.
 *
 * ## ⚠ 2026-09-22 정정 — `kind:'room'` 을 못 그려 「기록이 없습니다」가 되던 것
 *
 * 처음 판은 `name` 과 `email` 만 봤다. 그런데 서버 계약(`sends.repo.ts:28-36`)에는 **`room`** 이
 * 있고, 메시지 갈래가 **단체방 하나**로 확정됐으니 그 줄이 실제로 온다. 방 줄은 이름도 주소도
 * 없을 수 있어서, 옛 판은 **「받는 사람 기록이 없습니다」**로 그렸다 — 실제로는 «방으로 보낸 것»이다.
 *
 * ⛔ 주소를 가리지 않는다 — 가린 주소는 되짚을 수 없고, 「외부 주소 아무나」가 이미 결정이다.
 */
export function recipientLabel(r: MinutesSendRecipient): string {
  const name = (r.name ?? '').trim();
  const email = (r.email ?? '').trim();
  if (r.kind === 'room') {
    // 방 이름을 서버가 실어 주면 그것을, 아니면 「대화방」이라고만 적는다(id 는 사람에게 뜻이 없다).
    return name !== '' ? `${tMinutes('minutes.send.room_label')} ${name}` : tMinutes('minutes.send.room_label');
  }
  if (name !== '') return name;
  if (email !== '') return email;
  /*
   * ★ 이름도 주소도 없는 `user` 줄 — 서버가 표시 이름을 못 실은 경우다. **빈 글자로 버리지 않는다**:
   *   버리면 「3명에게 보냈는데 이력엔 1명」이 되어 «되짚을 길»이 조용히 줄어든다.
   */
  const id = (r.id ?? '').trim();
  return id !== '' ? tMinutes('minutes.send.unnamed_user', { id }) : '';
}

export function MinutesSentHistory({ hook }: { hook: MinutesSendsHook }) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const body = (): JSX.Element => {
    if (hook.state === 'loading') return <p className="minutes-muted">{tMinutes('minutes.list.loading')}</p>;
    if (hook.state === 'not-served') return <p className="minutes-muted">{tMinutes('minutes.send.history_not_ready')}</p>;
    // ⛔ 위와 «다른 말»이다 — 기다려서 될 일이 아니다.
    if (hook.state === 'gone') return <p className="minutes-muted">{tMinutes('minutes.send.history_gone')}</p>;
    if (hook.state === 'error') return <p className="minutes-muted">{tMinutes('minutes.send.history_failed')}</p>;
    if (hook.items.length === 0) return <p className="minutes-muted">{tMinutes('minutes.send.history_empty')}</p>;

    const timelineItems: TimelineItem[] = hook.items.map((rec) => {
      const labels = rec.recipients.map(recipientLabel).filter((s) => s !== '');
      const open = expanded.has(rec.id);
      const trail = labels.length === 0
        ? tMinutes('minutes.send.history_no_recipient')
        : open ? labels.join(' · ') : formatRecipientTrail(labels, 2);
      return {
        id: rec.id,
        title: (
          <span className="msend-hist-row">
            <Badge variant="default" size="sm">{tMinutes(CHANNEL_KEY[rec.channel])}</Badge>
            <span className="msend-hist-who">{trail}</span>
            {labels.length > 2 && (
              <button type="button" className="msend-hist-more" onClick={() => toggle(rec.id)}>
                {open ? tMinutes('minutes.send.history_collapse') : tMinutes('minutes.send.history_expand')}
              </button>
            )}
          </span>
        ),
        at: rec.sent_at,
        atLabel: formatDateTime(rec.sent_at),
        tone: 'info',
      };
    });
    return <Timeline items={timelineItems} />;
  };

  return (
    <section className="minutes-block msend-hist">
      <div className="minutes-block-head">
        <h3><span className="minutes-block-ico"><Clock size={14} aria-hidden /></span> {tMinutes('minutes.send.history')}</h3>
      </div>
      {body()}
    </section>
  );
}
